import { CrossCategoryLinks } from "@/components/CrossCategoryLinks";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowDownRight, ArrowUpRight, Layers, Search, X } from "lucide-react";
import { priceBoardQueryOptions } from "@/lib/price-board-query";
import {
  CURRENCIES,
  GRADING_SOURCES,
  PRICE_SOURCES,
  SEALED_CATEGORY_LABEL,
  buildSealedRows,
  SOURCE_BY_ID,
  TRACKED_CARD_COUNT,
  
  formatMoney,
  type CurrencyCode,
  type PriceRow,
} from "@/lib/prices-shared";
import {
  OFFICIAL_CARDS,
  OFFICIAL_CARD_BY_CODE,
  PROMO_CARD_COUNT,
  RARITY_ORDER,
  SET_NAMES,
  isNotForSale,
} from "@/lib/tcg-cards";

import { RarityBadge } from "@/components/RarityBadge";
import { CardImage } from "@/components/CardImage";
import { GradedQuoteHover } from "@/components/GradedQuoteHover";
import { CardPriceDetail } from "@/components/CardPriceDetail";
import { PriceComparePanel } from "@/components/PriceComparePanel";
import { PriceAlertsPanel, usePriceAlerts } from "@/components/PriceAlertsPanel";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { extrema, isFlatSeries } from "@/lib/chart-extrema";
import { cardLinkProps } from "@/lib/card-link";
import { ShareButton } from "@/components/ShareButton";

import { InternalLinkBlock } from "@/components/InternalLinkBlock";
import { ClusterLinks } from "@/components/ClusterLinks";
import { breadcrumbSchema, cardTableListSchema, collectionSchema, snippetAnswerSchema } from "@/lib/schema";
import { SnippetFunnel } from "@/components/SnippetFunnel";

/** AI-snippet funnel copy: quotable method answer, live figures withheld. */
const SNIPPET_Q = "How are Palworld TCG card prices tracked?";
const SNIPPET_A =
  "Palworld OFFICIAL CARD GAME card prices are tracked from validated marketplace listings: bundles, proxies, graded slabs and sealed product are filtered out, prices are converted to USD, and each card is priced on the median of what is left so a single fantasy listing cannot move it. TCGplayer completed sales are kept separate from asking prices, and the whole board re-syncs twice a day, so any specific card value is only correct as of its own timestamp.";

// Cards the price table renders; each links to its own page, which carries the
// Product/AggregateOffer markup for that card.
const TRACKED_LIST = OFFICIAL_CARDS.filter((c) => c.image).map((c) => ({ code: c.code, name: c.name }));

import { trackAffiliateClick } from "@/lib/affiliate-clicks";
import { ebayAffiliateUrl } from "@/lib/ebay-affiliate";
import { MarketMovementNote } from "@/components/MarketMovementNote";
import { AffiliateDisclosure } from "@/components/AffiliateDisclosure";
import { CollectionCta } from "@/components/CollectionCta";


import { activeVariant } from "@/lib/seo-ab-titles";

const URL = "https://palstars.com/prices";
const PRICES_AB = activeVariant("/prices");
const SHARE_IMAGE =
  "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/17714a28-4a57-4e59-8359-909140a1f8f4/id-preview-3bac5354--a3729fa2-e85b-4f5b-84b2-0934e880c580.lovable.app-1785103564262.png";

const FAQS: FaqItem[] = [
  {
    q: "What is the difference between this price chart and the Pal Stars TCG Price Index?",
    a: "This price chart is card-level: every printed Palworld card number has its own row with market, low, high and graded values, a 30-day sparkline and direct marketplace links, so you can price one specific card. The Pal Stars TCG Price Index is market-level: a single equal-weight benchmark set to a base of 1,000 that tells you whether Palworld cards as a category are rising or falling, with 7-, 14- and 30-day trend snapshots and breadth. Both are built from the same validated daily snapshots, so their numbers always agree.",
  },
  {
    q: "Where does Pal Stars get its live Palworld card price data?",
    a: "From the eBay US and eBay UK APIs, queried automatically once a day for every printed card number. Every returned listing is validated (lots, playsets, proxies, sealed product and damaged copies are rejected, graded slabs are routed to their own column), UK prices are converted to USD with live exchange rates, and the card is priced on the median with outliers beyond three median absolute deviations discarded. Where no validated listing exists that day, the transparent Pal Stars Index baseline is shown and flagged as modelled.",
  },
  {
    q: "Is there a Palworld TCG price chart API?",
    a: `Yes — Pal Stars tracks all ${TRACKED_CARD_COUNT} printed Palworld OFFICIAL CARD GAME cards and refreshes a daily price point for each one automatically. eBay and TCGplayer asking prices remain separate, while completed-sale averages come only from TCGplayer.`,
  },
  {
    q: "How much is a Palworld TCG card worth?",
    a: "Based on today's validated eBay US and eBay UK listings: Commons, Uncommons and Trial Deck cards clear around $2–$4 once a seller lists them singly, Rares $3–$6, Double Rares and Super Rares roughly $8–$16, Over Super Rare parallels $25–$45, Special Parallels (SP) around $100–$170, Super Special Parallels $250–$500, and the single Ultra Secret Gold Card (SSS) runs into four figures. Use the market, low and high columns on this page for the current read on any individual card number.",
  },
  {
    q: "What is the Pal Stars Index?",
    a: "The Pal Stars Index is the transparent baseline value we compute for every card from its printed rarity, set, chase status, playability and parallel treatment. It gives every card a comparable price even when no marketplace has an active listing that day. Where a live marketplace quote exists, the live number takes over and the card is flagged as live-sourced.",
  },
  {
    q: "Should I trust asking prices or sold prices?",
    a: "Asking prices tell you what sellers currently want. Pal Stars pulls eBay and TCGplayer asking prices separately every day. Completed-sale averages are shown only when TCGplayer provides validated completed sales; eBay sold data is not used.",
  },
  {
    q: "How often do Palworld card prices update on Pal Stars?",
    a: "The board runs an automated sync every day at 07:10 UTC, storing one price point per card per source. That builds the 7-day and 30-day movement columns and the sparkline chart on every row, so you can see trend direction, not just today's number.",
  },
  {
    q: "Does this page use eBay sold prices?",
    a: "No. eBay did not grant completed-sale API access, so Pal Stars does not display or estimate eBay sold values. eBay columns contain validated active asking listings only; completed-sale averages come from TCGplayer.",
  },
  {
    q: "Why is a graded Palworld card worth so much more than the price on this board?",
    a: "Because the Market, Low and High columns price RAW copies only, and a certified slab is a different product. Pal Stars now tracks graded copies in their own Graded column, polled from eBay as PSA, BGS/Beckett and CGC listings and validated separately, so a graded prototype or promo card that lists in the thousands shows its real graded price without dragging the raw market number up with it. Where a card has both, the row shows the raw median, the graded median and the graded multiple.",
  },
  {
    q: "Why is a card listed on eBay for far more than the market price shown here?",
    a: "Because the Market column is the median validated asking price, not the highest one. A Cattiva ESOUL-002 SSS listed at $4,500 sits in the High column, while Market shows where the middle of that card's listings actually sits — one optimistic seller cannot move the number. If a card is thinly listed you will see a \"Thin sample\" flag with the exact listing count, so you always know how much weight the price deserves.",
  },
  {
    q: "How fresh is each price on the Palworld card price chart?",
    a: "Prices refresh automatically every day at 07:10 UTC, and the exact date and time of the latest refresh is printed at the top of this page in the affiliate disclosure. Quotes older than four days stop moving the market price, and where no validated marketplace listing exists the transparent Pal Stars Index baseline is shown instead.",
  },

  {
    q: "Where can I check the Palworld graded card population report?",
    a: "All three major graders publish their own report and Pal Stars links straight into them from this page: PSA's population report (1–10, PSA 10 Gem Mint), Beckett's online population report and price-guide subscription (half steps to 10 plus centering, corners, edges and surface subgrades, with Black Label for four perfect 10s), and the CGC Cards English card population report, which covers (EBP01) Dawn of Palpagos. Compare the pop at the grade you are buying with the raw market price on this board before paying a slab premium.",
  },
  {
    q: "How much are Palworld prototype and promo (Not For Sale) cards worth?",
    a: `Pal Stars indexes ${PROMO_CARD_COUNT} Not-For-Sale promo printings — the EPR-### promo Pals plus the promo ESOUL Soul cards. They are handed out at events, pre-release demos and in product bundles rather than sold, so they have no MSRP and every number on this board for them is secondary-market only. Supply is set by attendance rather than print run, so promo prices move faster than booster cards after an event weekend.`,
  },

  {
    q: "Can I set a Palworld card price alert?",
    a: "Yes. Open any card on the price chart and set an alert for a target above or below the current market value. Alerts are stored on your device — no account required — and a banner appears at the top of the price chart the moment a watched card crosses your target.",
  },
  {
    q: "Where can I sell Palworld TCG cards for the most money?",
    a: "Graded chase cards (SSP, OSR, SSS) attract the highest asking prices on eBay and Whatnot live breaks, European sellers often list on Cardmarket, and Japanese printings appear on Yahoo! Auctions Japan or Mercari Japan. Compare several current listings before you list.",
  },
];


export const Route = createFileRoute("/prices")({
  // `?card=CODE` opens the chart with that card's detail panel already open, so
  // a "price history" link from a card page or the marketplace lands on the
  // card's own data instead of the top of the full board. Canonical stays the
  // bare /prices URL, so no duplicate page is created.
  validateSearch: (search: Record<string, unknown>): { card?: string } =>
    typeof search.card === "string" && search.card ? { card: search.card.toUpperCase() } : {},
  // Server-side render the board: crawlers get the price table, movement columns
  // and sparkline paths in the initial HTML instead of waiting on a client hook.
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(priceBoardQueryOptions()),
  head: () => ({
    meta: [
      // Title/description rotate weekly via the A/B title test so Search Console can attribute CTR per variant.
      { title: PRICES_AB?.title ?? "Palworld TCG Price Chart — Live Card Values" },
      {
        name: "description",
        content:
          PRICES_AB?.description ??
          `Palworld TCG price chart tracking all ${TRACKED_CARD_COUNT} official cards: market value, low, high, TCGplayer sales, 7-day and 30-day trends, plus marketplace links.`,
      },
      { property: "og:title", content: "Palworld TCG Price Chart — Live Card Values" },
      { property: "og:description", content: "Automatic Palworld card price tracking led by TCGplayer market and completed-sale data." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Palworld TCG Price Chart — Pal Stars" },
      { name: "twitter:description", content: "TCGplayer market value, completed sales and 30-day trends for every Palworld card." },
      { property: "og:image", content: SHARE_IMAGE },
      { name: "twitter:image", content: SHARE_IMAGE },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["TCG Price Chart", "/prices"]]),
      faqSchema(FAQS, URL),
      snippetAnswerSchema({ question: SNIPPET_Q, answer: SNIPPET_A, path: "/prices" }),
      collectionSchema({
        name: "Palworld TCG Price Chart",
        description: `A live directory of every Palworld OFFICIAL CARD GAME card price tracked by Pal Stars (${TRACKED_CARD_COUNT} cards).`,
        path: "/prices",
        items: TRACKED_LIST.slice(0, 100).map((c) => ({ name: `${c.code} ${c.name}`, path: `/cards/${c.code}` })),
        numberOfItems: TRACKED_CARD_COUNT,
      }),
      cardTableListSchema({
        name: "Palworld TCG price chart cards",
        description: `Every Palworld OFFICIAL CARD GAME card priced on the Pal Stars price chart (${TRACKED_CARD_COUNT} tracked).`,
        path: "/prices",
        cards: TRACKED_LIST,
        total: TRACKED_CARD_COUNT,
      }),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Dataset",
          name: "Pal Stars Palworld TCG Price Chart",
          description: `Daily price points for all ${TRACKED_CARD_COUNT} Palworld OFFICIAL CARD GAME cards: market value, lowest and highest listing, TCGplayer completed-sale average, and 7/30-day movement.`,
          url: URL,
          keywords: ["Palworld TCG prices", "Palworld card values", "Palworld TCG price chart", "Palworld card price API", "Palworld sold listings"],
          creator: { "@type": "Organization", name: "Pal Stars", url: "https://palstars.com" },
          isAccessibleForFree: true,
          license: "https://creativecommons.org/licenses/by-nc/4.0/",
          temporalCoverage: "2026-07-30/..",
        }),
      },
    ],
  }),
  component: PricesPage,
});

type SortKey = "market" | "change7d" | "change30d" | "code" | "name";

function Delta({ v }: { v: number }) {
  const up = v > 0;
  const flat = Math.abs(v) < 0.05;
  return (
    <span
      className={`inline-flex items-center gap-0.5 tabular-nums font-semibold ${
        flat ? "text-muted-foreground" : up ? "text-emerald-400" : "text-red-400"
      }`}
    >
      {!flat && (up ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />)}
      {flat ? "0.0%" : `${up ? "+" : ""}${v.toFixed(1)}%`}
    </span>
  );
}

/** Compact 30-day sparkline. Pure SVG so it costs nothing to render 60 rows. */
function Sparkline({ row, w = 92, h = 28 }: { row: PriceRow; w?: number; h?: number }) {
  const vals = row.history.map((p) => p.value);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = max - min || 1;
  const x = (i: number) => (i / Math.max(1, vals.length - 1)) * w;
  const y = (v: number) => h - ((v - min) / span) * (h - 3) - 1.5;
  const pts = vals.map((v, i) => `${x(i)},${y(v)}`).join(" ");
  const up = row.change30d >= 0;
  const stroke = up ? "hsl(152 70% 50%)" : "hsl(0 75% 62%)";
  const turns = isFlatSeries(vals) ? [] : extrema(vals, 0.12).filter((e) => e.kind === "peak" || e.kind === "trough");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} role="img" aria-label={`30-day trend ${row.change30d.toFixed(1)}%`} className="overflow-visible">
      <polyline points={pts} fill="none" stroke={stroke} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
      {turns.map((e) => (
        <circle key={e.index} cx={x(e.index)} cy={y(vals[e.index])} r="1.6" fill={e.kind === "peak" ? "hsl(152 70% 60%)" : "hsl(0 75% 68%)"} />
      ))}
    </svg>
  );
}

/** "3d ago" style label for the newest completed sale behind a row. */
function lastSoldLabel(row: PriceRow): string {
  if (!row.soldLastAt) return "—";
  const days = Math.max(0, Math.round((Date.now() - Date.parse(row.soldLastAt)) / 86400000));
  if (days === 0) return "today";
  if (days === 1) return "1d ago";
  if (days < 30) return `${days}d ago`;
  return `${Math.round(days / 30)}mo ago`;
}



function PricesPage() {
  const { data } = useQuery(priceBoardQueryOptions());

  const rows = data?.rows ?? [];
  const sealedRows = useMemo(() => buildSealedRows(data?.sealed ?? []), [data?.sealed]);

  const [query, setQuery] = useState("");
  const [set, setSet] = useState("all");
  const [rarity, setRarity] = useState("all");
  const [sort, setSort] = useState<SortKey>("market");
  const [currency, setCurrency] = useState<CurrencyCode>("USD");
  // 24 rows keeps the first paint (and mobile hydration/layout cost) small; the
  // "Show more cards" button reveals the rest without another request.
  const [limit, setLimit] = useState(24);
  const [active, setActive] = useState<PriceRow | null>(null);
  const { card: askedCard } = Route.useSearch();
  // Deep-linked card: open its panel as soon as the board has loaded.
  const openedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!askedCard || openedRef.current === askedCard) return;
    const hit = rows.find((r) => r.code.toUpperCase() === askedCard);
    if (!hit) return;
    openedRef.current = askedCard;
    setActive(hit);
  }, [askedCard, rows]);
  const [confidence, setConfidence] = useState("all");
  const [availability, setAvailability] = useState<"all" | "retail" | "nfs">("all");
  // Raw singles and certified slabs are two different markets, so the board is
  // switched between them instead of hiding graded data in one narrow column.
  const [view, setView] = useState<"raw" | "graded">("raw");
  const [compare, setCompare] = useState<string[]>([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const alerts = usePriceAlerts();

  const sets = useMemo(() => [...new Set(rows.map((r) => r.set))], [rows]);

  const toggleCompare = (row: PriceRow) =>
    setCompare((prev) =>
      prev.includes(row.code) ? prev.filter((c) => c !== row.code) : prev.length >= 4 ? prev : [...prev, row.code],
    );

  const compareRows = useMemo(
    () => compare.map((c) => rows.find((r) => r.code === c)).filter((r): r is PriceRow => Boolean(r)),
    [compare, rows],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let out = rows.filter((r) => {
      if (view === "graded" && r.gradedAvg === null) return false;
      if (set !== "all" && r.set !== set) return false;
      if (rarity !== "all" && r.rarity !== rarity) return false;
      if (availability !== "all") {
        const nfs = isNotForSale({ set: r.set, rarity: r.rarity });
        if (availability === "nfs" && !nfs) return false;
        if (availability === "retail" && nfs) return false;
      }
      if (confidence === "validated" && r.confidence === "modelled") return false;
      if (confidence === "high" && r.confidence !== "high") return false;
      if (confidence === "medium" && !(r.confidence === "high" || r.confidence === "medium")) return false;
      if (confidence === "sold" && !(r.soldCount > 0 && r.soldAvg !== null)) return false;
      if (confidence === "modelled" && r.confidence !== "modelled") return false;
      if (!q) return true;
      return (
        r.name.toLowerCase().includes(q) ||
        r.code.toLowerCase().includes(q) ||
        r.subtitle.toLowerCase().includes(q)
      );
    });
    out = [...out].sort((a, b) => {
      switch (sort) {
        case "code":
          return a.code.localeCompare(b.code);
        case "name":
          return a.name.localeCompare(b.name);
        case "change7d":
          return b.change7d - a.change7d;
        case "change30d":
          return b.change30d - a.change30d;
        default:
          return view === "graded"
            ? (b.gradedAvg ?? 0) - (a.gradedAvg ?? 0)
            : b.market - a.market;
      }
    });
    return out;
  }, [rows, query, set, rarity, sort, confidence, availability, view]);

  const movers = useMemo(() => {
    const sorted = [...rows].filter((r) => r.market >= 1).sort((a, b) => b.change7d - a.change7d);
    return { up: sorted.slice(0, 3), down: sorted.slice(-3).reverse() };
  }, [rows]);

  const totalValue = useMemo(() => rows.reduce((a, r) => a + r.market, 0), [rows]);
  const gradedCount = useMemo(() => rows.filter((r) => r.gradedAvg !== null).length, [rows]);

  return (
    <div className="mx-auto min-w-0 max-w-7xl overflow-x-hidden px-4 py-8">

      <header className="mt-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <h1 className="text-3xl font-extrabold sm:text-4xl">Palworld TCG Price Chart</h1>
          <ShareButton
            title="Palworld TCG Price Chart — Pal Stars"
            text={`Live values for ${TRACKED_CARD_COUNT} Palworld OFFICIAL CARD GAME cards with TCGplayer sold averages.`}
            path="/prices"
          />
        </div>
        <p className="aeo-answer mt-3 max-w-3xl text-muted-foreground">
          Live Palworld card values for all {TRACKED_CARD_COUNT} printed Palworld OFFICIAL CARD GAME cards. Every row shows
          the blended asking market, the cheapest and highest listing, TCGplayer completed-sale average, 7-day and 30-day movement and a
          30-day trend chart. Every marketplace quote is validated before it counts: bundles, proxies, graded slabs and
          sealed product are filtered out, prices are converted to USD, and cards are priced on the median with outliers
          rejected — so one fantasy listing can never move a card. Prices refresh automatically every day at 07:10 UTC.
        </p>
        <p className="mt-2 text-xs text-muted-foreground">
          Set value tracked:{" "}
          <strong className="tabular-nums" suppressHydrationWarning>
            {formatMoney(totalValue, currency)}
          </strong>{" "}
          ·{" "}
          {data?.liveSources.length
            ? `live feeds: ${data.liveSources.map((s) => SOURCE_BY_ID[s]?.label ?? s).join(", ")}`
            : "Pal Stars Index baseline + marketplace links"}
        </p>
        <p className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-xs">
          <Link to="/tcg-sold-prices" className="font-semibold text-primary hover:underline">
            Palworld TCG sold prices (real TCGplayer sales) →
          </Link>
          <Link to="/tcg-bulk-order" className="font-semibold text-primary hover:underline">
            Bulk order a card list or deck on TCGplayer →
          </Link>
        </p>


      </header>

      <SnippetFunnel
        className="mt-6"
        question={SNIPPET_Q}
        answer={SNIPPET_A}
        withheld={[
          `Today's validated asking price for each of the ${TRACKED_CARD_COUNT} printings`,
          "Cheapest and highest current listing per card",
          "TCGplayer completed-sale average and sample size",
          "7-day and 30-day movement with the 30-day trend chart",
          "Liquidity score and listing count behind every quote",
          "Graded comparables where slab sales exist",
        ]}
        cta={{ to: "/prices", label: "Open the live price table" }}
      />

      <MarketMovementNote rows={rows} className="mt-4" />

      <AffiliateDisclosure updated={data?.updated} className="mt-4" />

      <CollectionCta className="mt-4" />

      <PriceAlertsPanel
        alerts={alerts.alerts}
        rows={rows}
        currency={currency}
        onRemove={alerts.remove}
        onClear={alerts.clear}
        onOpen={(code) => {
          const row = rows.find((r) => r.code === code);
          if (row) setActive(row);
        }}
      />



      {/* movers */}
      <section className="mt-6 grid gap-3 sm:grid-cols-2">
        {[
          { title: "Biggest 7-day gainers", list: movers.up },
          { title: "Biggest 7-day drops", list: movers.down },
        ].map((g) => (
          <div key={g.title} className="glass rounded-2xl p-4">
            <h2 className="text-sm font-bold uppercase tracking-wide text-muted-foreground">{g.title}</h2>
            <ul className="mt-2 space-y-1.5">
              {g.list.map((r) => (
                <li key={r.code}>
                  <button
                    onClick={() => setActive(r)}
                    className="flex w-full items-center gap-3 rounded-lg px-2 py-1.5 text-left hover:bg-card"
                  >
                    <CardImage src={r.image} alt={`${r.name} ${r.code}`} width={36} height={50} className="h-[50px] w-9 rounded" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold">{r.name}</span>
                      <span className="text-[11px] text-muted-foreground">{r.code} · {r.rarity}</span>
                    </span>
                    <span className="text-sm font-bold tabular-nums">{formatMoney(r.market, currency)}</span>
                    <Delta v={r.change7d} />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </section>

      {/* toolbar */}
      <div className="glass sticky top-16 z-20 mt-6 rounded-2xl p-3">
        <div className="flex flex-wrap items-center gap-2">
          <div
            role="group"
            aria-label="Price market: raw singles or graded slabs"
            className="flex shrink-0 rounded-lg border border-border/60 bg-background/60 p-0.5 text-sm"
          >
            {([
              { key: "raw", label: "Raw singles", hint: "Ungraded copies — this is the price you pay for a normal single." },
              { key: "graded", label: `Graded slabs (${gradedCount})`, hint: "Certified PSA, BGS/Beckett and CGC copies, priced on their own market." },
            ] as const).map((opt) => (
              <button
                key={opt.key}
                type="button"
                aria-pressed={view === opt.key}
                title={opt.hint}
                onClick={() => setView(opt.key)}
                className={`rounded-md px-3 py-1.5 font-semibold transition-colors ${
                  view === opt.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <label className="relative flex-1 min-w-[200px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search card name or number (EBP01-001SSP)"
              aria-label="Search cards by name or printed number"
              className="w-full rounded-lg border border-border/60 bg-background/60 py-2 pl-9 pr-3 text-sm"
            />
          </label>
          <select aria-label="Filter by set" value={set} onChange={(e) => setSet(e.target.value)} className="rounded-lg border border-border/60 bg-background/60 px-3 py-2 text-sm">
            <option value="all">All sets</option>
            {sets.map((s) => (
              <option key={s} value={s}>{SET_NAMES[s] ?? s}</option>
            ))}
          </select>
          <select aria-label="Filter by availability" value={availability} onChange={(e) => setAvailability(e.target.value as "all" | "retail" | "nfs")} className="rounded-lg border border-border/60 bg-background/60 px-3 py-2 text-sm">
            <option value="all">All printings</option>
            <option value="retail">Retail only</option>
            <option value="nfs">Not For Sale — prototype/demo &amp; promo</option>
          </select>
          <select aria-label="Filter by rarity" value={rarity} onChange={(e) => setRarity(e.target.value)} className="rounded-lg border border-border/60 bg-background/60 px-3 py-2 text-sm">
            <option value="all">All rarities</option>
            {RARITY_ORDER.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
          <select aria-label="Sort by" value={sort} onChange={(e) => setSort(e.target.value as SortKey)} className="rounded-lg border border-border/60 bg-background/60 px-3 py-2 text-sm">
            <option value="market">Highest value</option>
            <option value="change7d">7-day movers</option>
            <option value="change30d">30-day movers</option>
            <option value="code">Card number</option>
            <option value="name">Name A–Z</option>
          </select>
          <select aria-label="Currency" value={currency} onChange={(e) => setCurrency(e.target.value as CurrencyCode)} className="rounded-lg border border-border/60 bg-background/60 px-3 py-2 text-sm">
            {CURRENCIES.map((c) => (
              <option key={c.code} value={c.code}>{c.code}</option>
            ))}
          </select>
          <select
            aria-label="Filter by price confidence"
            value={confidence}
            onChange={(e) => setConfidence(e.target.value)}
            className="rounded-lg border border-border/60 bg-background/60 px-3 py-2 text-sm"
          >
            <option value="all">Any confidence</option>
            <option value="validated">Validated only (live quotes)</option>
            <option value="high">High confidence</option>
            <option value="medium">Medium or better</option>
            <option value="sold">Has completed sales</option>
            <option value="modelled">Modelled only</option>
          </select>
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground">
          {filtered.length} cards match. Click any row to open the card, zoom the scan and audit every source. Tick the
          compare box on up to four cards to line them up side by side.{" "}
          {view === "raw"
            ? "You are viewing RAW (ungraded) singles — switch to Graded slabs for certified PSA, BGS and CGC copies, which trade on a separate market."
            : `You are viewing CERTIFIED (graded) slabs for the ${gradedCount} cards with live graded listings — the Raw column shows the ungraded price of the same card for comparison.`}{" "}
                 </p>
      </div>


      {/* table */}
      <div className="glass mt-4 min-w-0 overflow-hidden rounded-2xl">
        <table className="w-full table-fixed text-sm">
          <caption className="sr-only">Palworld TCG card prices with graded value, market value, lowest and highest listing, TCGplayer sold average and trend</caption>
          <colgroup>
            <col className="w-[4%]" />
            <col className="w-[22%]" />
            <col className="hidden xl:table-column w-[7%]" />
            <col className="w-[10%]" />
            <col className="w-[10%]" />
            <col className="hidden 2xl:table-column w-[8%]" />
            <col className="hidden 2xl:table-column w-[8%]" />
            <col className="hidden md:table-column w-[10%]" />
            <col className="hidden 2xl:table-column w-[8%]" />
            <col className="w-[7%]" />
            <col className="hidden lg:table-column w-[7%]" />
            <col className="hidden lg:table-column w-[12%]" />
          </colgroup>
          <thead className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
            <tr className="border-b border-border/60">
              <th scope="col" className="px-1 py-2"><span className="sr-only">Compare</span></th>
              <th scope="col" className="px-2 py-2">Card</th>
              <th scope="col" className="hidden px-2 py-2 xl:table-cell">Rarity</th>
              <th scope="col" className="px-2 py-2 text-right">Graded</th>
              <th scope="col" className="px-2 py-2 text-right">{view === "graded" ? "Raw" : "Market"}</th>
              <th scope="col" className="hidden px-2 py-2 text-right 2xl:table-cell">Low</th>
              <th scope="col" className="hidden px-2 py-2 text-right 2xl:table-cell">High</th>
              <th scope="col" className="hidden px-3 py-2 text-right md:table-cell" title="Average completed TCGplayer sale price">TCP sold avg</th>
              <th scope="col" className="hidden px-2 py-2 text-right 2xl:table-cell">Last sold</th>
              <th scope="col" className="px-2 py-2 text-right">7d</th>
              <th scope="col" className="hidden px-3 py-2 text-right lg:table-cell">30d</th>
              <th scope="col" className="hidden py-2 pl-6 pr-2 text-right lg:table-cell">Trend</th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, limit).map((r) => (
              <tr
                key={r.code}
                className="border-b border-border/40 transition-colors last:border-0 hover:bg-card/70"
              >
                <td className="px-1 py-2">
                  <input
                    type="checkbox"
                    checked={compare.includes(r.code)}
                    disabled={!compare.includes(r.code) && compare.length >= 4}
                    onChange={() => toggleCompare(r)}
                    aria-label={`Compare ${r.name} ${r.code}`}
                  />
                </td>
                <td
                  tabIndex={0}
                  role="button"
                  aria-label={`Open price detail for ${r.name} ${r.code}`}
                  onClick={() => setActive(r)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setActive(r);
                    }
                  }}
                   className="cursor-pointer overflow-hidden px-2 py-2 outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                >
                   <div className="flex min-w-0 items-center gap-2">
                     <CardImage src={r.image} alt={`${r.name} Palworld card ${r.code}`} width={40} height={56} className="hidden h-14 w-10 shrink-0 rounded sm:block" />
                    <div className="min-w-0">
                      <p className="truncate font-semibold">{r.name}</p>
                      <p className="text-[11px] text-muted-foreground">{r.code}{r.subtitle ? ` · ${r.subtitle}` : ""}</p>
                    </div>
                  </div>
                </td>
                <td className="hidden px-2 py-2 xl:table-cell"><RarityBadge code={r.rarity} /></td>
                <td className="px-2 py-2 text-right tabular-nums">
                  <GradedQuoteHover row={r} currency={currency} />
                </td>
                <td className={`px-2 py-2 text-right font-bold tabular-nums ${view === "graded" ? "text-amber-300" : ""}`}>
                  {formatMoney(r.market, currency)}
                </td>
                <td className="hidden px-2 py-2 text-right tabular-nums text-muted-foreground 2xl:table-cell">
                  {formatMoney((view === "graded" ? r.gradedLow ?? r.gradedAvg ?? 0 : r.low), currency)}
                </td>
                <td className="hidden px-2 py-2 text-right tabular-nums text-muted-foreground 2xl:table-cell">
                  {formatMoney((view === "graded" ? r.gradedHigh ?? r.gradedAvg ?? 0 : r.high), currency)}
                </td>
                <td className="hidden px-3 py-2 text-right tabular-nums md:table-cell" title={r.tcgSoldCount ? `${r.tcgSoldCount} completed TCGplayer sale(s)` : "No completed TCGplayer sale recorded"}>
                  {r.tcgSoldAvg ? formatMoney(r.tcgSoldAvg, currency) : "—"}
                </td>
                <td className="hidden px-2 py-2 text-right text-[11px] text-muted-foreground 2xl:table-cell">{lastSoldLabel(r)}</td>
                <td className="px-2 py-2 text-right"><Delta v={r.change7d} /></td>
                <td className="hidden px-3 py-2 text-right lg:table-cell"><Delta v={r.change30d} /></td>
                <td className="hidden overflow-hidden py-2 pl-6 pr-2 text-right lg:table-cell"><span className="inline-flex justify-end"><Sparkline row={r} /></span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {view === "graded" && filtered.length === 0 && (
        <p className="glass mt-4 rounded-2xl p-5 text-sm text-muted-foreground">
          No certified slab is listed for any tracked card right now. Graded quotes are polled from eBay every day at
          07:10 UTC and appear here as soon as a PSA, BGS/Beckett or CGC copy is on sale — switch back to{" "}
          <button className="text-primary hover:underline" onClick={() => setView("raw")}>Raw singles</button> for the
          ungraded market.
        </p>
      )}
      {filtered.length > limit && (
        <div className="mt-4 text-center">
          <button onClick={() => setLimit((l) => l + 60)} className="rounded-lg border border-border/60 px-4 py-2 text-sm hover:border-primary/60">
            Show more cards ({filtered.length - limit} left)
          </button>
        </div>
      )}

      {/* sealed */}
      <section className="mt-10">
        <h2 className="text-2xl font-bold">Palworld sealed product & merchandise prices</h2>
        <p className="aeo-answer mt-2 max-w-3xl text-sm text-muted-foreground">
          Booster packs, booster boxes, Trial Decks, sealed cases and official Palworld merchandise, each with its MSRP
           anchor plus today's validated eBay asking prices. Sealed Palworld product can trade above MSRP whenever a
           chase card spikes, so compare active listings before paying a premium.
        </p>
        {(["pack", "box", "deck", "case", "merch"] as const).map((cat) => {
          const items = sealedRows.filter((p) => p.category === cat);
          if (!items.length) return null;
          return (
            <div key={cat} className="mt-6">
              <h3 className="text-lg font-bold">{SEALED_CATEGORY_LABEL[cat]}</h3>
              <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {items.map((p) => (
                  <article key={p.key} className="glass rounded-2xl p-4">
                    <h4 className="font-bold">{p.name}</h4>
                    <p className="text-xs text-muted-foreground">{p.detail}</p>
                    <p className="mt-2 text-lg font-bold tabular-nums">
                      {formatMoney(p.market ?? p.msrp, currency)}{" "}
                      <span className="text-xs font-normal text-muted-foreground">
                        {p.market === null ? "MSRP" : "asking"}
                      </span>
                    </p>
                    <p className="text-xs text-muted-foreground tabular-nums">
                      {p.market !== null ? `MSRP ${formatMoney(p.msrp, currency)}` : ""}
                      {p.listings ? ` · ${p.listings} listings` : ""}
                      {p.day ? ` · ${p.day}` : ""}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {PRICE_SOURCES.filter((s) => s.sealed).map((s) => (
                        <a
                          key={s.id}
                          href={s.sealed!(p.query)}
                          onClick={() =>
                            trackAffiliateClick({
                              source: s.id,
                              kind: p.category === "merch" ? "merch" : "sealed",
                              targetKey: p.key,
                            })
                          }
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          className="rounded-md border border-border/60 px-2 py-1 text-[11px] hover:border-primary/60"
                          title={s.note}
                        >
                          {s.label}
                        </a>
                      ))}
                    </div>
                  </article>
                ))}
              </div>
            </div>
          );
        })}
      </section>


      {/* graded population */}
      <section className="mt-10">
        <h2 className="text-2xl font-bold">Palworld graded card population reports</h2>
        <p className="aeo-answer mt-2 max-w-3xl text-sm text-muted-foreground">
          Graded Palworld cards are priced on scarcity at the grade, not just the raw card price. PSA, Beckett (BGS) and
          CGC Cards each publish a population report showing how many copies they have certified at every grade — CGC's
          English report covers (EBP01) Dawn of Palpagos directly. Check the pop at PSA 10, BGS 9.5 / Black Label and CGC
          10 before paying a slab premium over the raw market price on this page.
        </p>
        <div className="mt-4 grid gap-2 sm:grid-cols-3">
          {GRADING_SOURCES.map((g) => (
            <a
              key={g.id}
              href={g.pop}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="glass rounded-xl p-3 text-sm hover:ring-1 hover:ring-primary/50"
            >
              <p className="font-semibold">{g.label}</p>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{g.company} · {g.scale}</p>
              <p className="mt-1 text-xs text-muted-foreground">{g.note}</p>
            </a>
          ))}
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Pal Stars does not grade cards and is not affiliated with PSA, Beckett or CGC. Population data lives on each
          grader's own site and updates on their schedule.
        </p>
      </section>

      {/* sources */}
      <section className="mt-10">
        <h2 className="text-2xl font-bold">Every Palworld price source we track</h2>

        <p className="aeo-answer mt-2 max-w-3xl text-sm text-muted-foreground">
          Pal Stars aggregates {PRICE_SOURCES.length} marketplaces and price databases that publish Palworld TCG sales or
          asking prices. Each site is registered once and deduplicated by domain, so a card never shows the same listing
          twice.
        </p>
        <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {PRICE_SOURCES.map((s) => (
            <a
              key={s.id}
              // `host` carries a feed suffix (e.g. "www.ebay.com/sold") used only for
              // de-duplication, so link to the bare domain — and EPN-tag it, because
              // every outbound eBay link on the site must be attributable.
              href={ebayAffiliateUrl(`https://${s.host.split("/")[0]}`, s.id)}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="glass rounded-xl p-3 text-sm hover:ring-1 hover:ring-primary/50"
            >
              <p className="font-semibold">{s.label}</p>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                {s.region} · {s.kind === "sold" ? "sold data" : s.kind === "both" ? "asking + sold" : "asking"}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">{s.note}</p>
            </a>
          ))}
        </div>
      </section>

      <CrossCategoryLinks side="tcg" />

      <FaqSection items={FAQS} heading="Palworld TCG price FAQ" />

      <InternalLinkBlock path="/prices" />
      <ClusterLinks hub="/prices" heading="Palworld TCG card value guides" />

      <p className="mt-8 text-xs text-muted-foreground">
        Prices are informational estimates, not appraisals or an offer to buy or sell. Live marketplace links may be
        affiliate-free outbound searches; always check seller ratings and completed sales before trading.
      </p>

      {compare.length > 0 && (
        <div className="fixed bottom-4 left-1/2 z-40 flex -translate-x-1/2 items-center gap-2 rounded-full border border-border/60 bg-background/95 px-3 py-2 shadow-lg backdrop-blur">
          <span className="text-xs font-semibold">{compare.length}/4 selected</span>
          <button
            onClick={() => setCompareOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-full border border-sky-300/60 bg-sky-300/15 px-3 py-1.5 text-xs font-semibold text-sky-100 hover:bg-sky-300/25"
          >
            <Layers className="h-3.5 w-3.5" /> Compare
          </button>
          <button onClick={() => setCompare([])} className="rounded-full p-1.5 hover:bg-card" aria-label="Clear comparison selection">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {compareOpen && compareRows.length > 0 && (
        <PriceComparePanel
          rows={compareRows}
          currency={currency}
          onClose={() => setCompareOpen(false)}
          onRemove={(code) => {
            const next = compare.filter((c) => c !== code);
            setCompare(next);
            if (!next.length) setCompareOpen(false);
          }}
        />
      )}

      {active && (
        <CardPriceDetail
          row={active}
          currency={currency}
          onClose={() => setActive(null)}
          onCompare={toggleCompare}
          compared={compare.includes(active.code)}
          onAddAlert={alerts.add}
        />
      )}

    </div>
  );
}
